/* eslint-disable @typescript-eslint/no-floating-promises */
import { formatUnits, ZeroAddress } from 'ethers'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { TouchableOpacity, View } from 'react-native'

import gasTankFeeTokens from '@ambire-common/consts/gasTankFeeTokens'
import { Network } from '@ambire-common/interfaces/network'
import { SubmittedAccountOp } from '@ambire-common/libs/accountOp/submittedAccountOp'
import { AccountOpStatus } from '@ambire-common/libs/accountOp/types'
import { resolveAssetInfo } from '@ambire-common/services/assetInfo'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import Badge from '@common/components/Badge'
import CopyIcon from '@common/assets/svg/CopyIcon'
import LinkIcon from '@common/assets/svg/LinkIcon'
import SkeletonLoader from '@common/components/SkeletonLoader'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import useToast from '@common/hooks/useToast'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import { setStringAsync } from '@common/utils/clipboard'
import { createTab } from '@web/extension-services/background/webapi/tab'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import { sizeMultiplier } from '@web/modules/sign-account-op/components/TransactionSummary'

import StatusBadge from './StatusBadge'
import getStyles from './styles'
import SubmittedOn from './SubmittedOn'

type Props = {
  network: Network
  size: 'sm' | 'md' | 'lg'
  isFeeLoading?: boolean
  /** RAILGUN ShieldBlocked: amber "Blocked" badge instead of red "Failed", plus
   * the recover/PPOI CTAs. Private detail sheet only; off for public. */
  isBlocked?: boolean
  ppoiUrl?: string
  onUnshieldToOrigin?: () => void
} & Pick<
  SubmittedAccountOp,
  'txnId' | 'identifiedBy' | 'accountAddr' | 'gasFeePayment' | 'status' | 'timestamp'
>

const Footer: FC<Props> = ({
  network,
  txnId,
  identifiedBy,
  accountAddr,
  gasFeePayment,
  status,
  size,
  timestamp,
  isFeeLoading = false,
  isBlocked = false,
  ppoiUrl,
  onUnshieldToOrigin
}) => {
  const { styles, theme } = useTheme(getStyles)
  const { addToast } = useToast()
  const { networks } = useNetworksControllerState()
  const { t } = useTranslation()
  const textSize = 14 * sizeMultiplier[size]
  const iconSize = 26 * sizeMultiplier[size]
  const iconSizeSm = 14 * sizeMultiplier[size]

  const canViewFee =
    status !== AccountOpStatus.Rejected &&
    status !== AccountOpStatus.BroadcastButStuck &&
    status !== AccountOpStatus.UnknownButPastNonce &&
    status !== AccountOpStatus.BroadcastedButNotConfirmed

  const { chainId } = network

  const [feeFormattedValue, setFeeFormattedValue] = useState<string>()
  const shouldShowFeeLoader =
    isFeeLoading ||
    (!!gasFeePayment &&
      !gasFeePayment.isSponsored &&
      !!gasFeePayment.inToken &&
      !!gasFeePayment.amount &&
      !feeFormattedValue)

  const handleViewTransaction = useCallback(async () => {
    if (!network.explorerUrl) {
      addToast(t("Can't open the transaction details because the network has no block explorer."), {
        type: 'error'
      })
      return
    }

    if (!txnId) {
      addToast(
        t("Can't open the transaction details because the transaction hash is not available yet."),
        { type: 'error' }
      )
      return
    }

    const base = network.explorerUrl.replace(/\/$/, '')
    const link = `${base}/tx/${txnId}`

    try {
      await createTab(link)
    } catch (e: any) {
      addToast(e?.message || 'Error opening explorer', { type: 'error' })
    }
  }, [network.explorerUrl, txnId, addToast, t])

  const handleCopyTxHash = useCallback(async () => {
    if (!txnId) return
    try {
      await setStringAsync(txnId)
      addToast(t('Transaction hash copied to clipboard'))
    } catch {
      addToast(t('Failed to copy'), { type: 'error' })
    }
  }, [txnId, addToast, t])

  useEffect((): void => {
    const feeTokenAddress = gasFeePayment?.inToken
    const nChainId =
      gasFeePayment?.feeTokenChainId ||
      // the rest is support for legacy data (no chainId recorded for the fee)
      (feeTokenAddress === ZeroAddress && chainId) ||
      gasTankFeeTokens.find((constFeeToken: any) => constFeeToken.address === feeTokenAddress)
        ?.chainId ||
      chainId

    // did is used to avoid tokenNetwork being Network | undefined
    // the assumption is that we can't pay the fee with token on network that is not present
    const tokenNetwork = networks.filter((n: Network) => n.chainId === nChainId)[0]

    const feeTokenAmount = gasFeePayment?.amount
    if (!feeTokenAddress || !tokenNetwork || !feeTokenAmount) {
      setFeeFormattedValue(undefined)
      return
    }

    setFeeFormattedValue(undefined)
    resolveAssetInfo(feeTokenAddress, tokenNetwork, ({ tokenInfo }) => {
      if (!tokenInfo || !gasFeePayment?.amount) return

      const fee = parseFloat(formatUnits(feeTokenAmount, tokenInfo.decimals))

      setFeeFormattedValue(`${formatDecimals(fee)} ${tokenInfo.symbol}`)
    }).catch((e) => {
      console.error(e)
      setFeeFormattedValue('Unknown. Please check the explorer.')
    })
  }, [
    networks,
    chainId,
    gasFeePayment?.feeTokenChainId,
    gasFeePayment?.amount,
    gasFeePayment?.inToken,
    addToast
  ])

  return (
    <View style={spacings.phMd}>
      {/* Two-row layout: top row keeps the badge left-aligned (with the "Transaction
          hash" label above) and lets Fee/SubmittedOn share the horizontal space; the
          action links move to their own wrapping row below so they never get squeezed
          into a 1-char-wide column on narrow phone-browser viewports. */}
      <View style={styles.footer}>
        <View style={[flexbox.directionRow, flexbox.alignStart]}>
          {isBlocked ? (
            <Badge size="md" type="warning" weight="medium" text={t('Blocked')} withRightSpacing />
          ) : (
            <StatusBadge status={status} textSize={textSize} />
          )}
          {canViewFee && (
            <View style={[flexbox.flex1, spacings.mrMd]}>
              <Text fontSize={textSize} appearance="secondaryText" weight="semiBold">
                {t('Fee')}:
              </Text>

              {gasFeePayment?.isSponsored ? (
                <Text fontSize={12} appearance="successText" weight="semiBold">
                  {t('Sponsored')}
                </Text>
              ) : (
                <Text fontSize={textSize} appearance="secondaryText">
                  {shouldShowFeeLoader ? (
                    <SkeletonLoader width={80} height={21} appearance="tertiaryBackground" />
                  ) : (
                    feeFormattedValue || '-'
                  )}
                </Text>
              )}
            </View>
          )}
          <SubmittedOn
            fontSize={textSize}
            iconSize={iconSize}
            chainId={network.chainId}
            timestamp={timestamp}
            numberOfLines={2}
          />
        </View>
        <View
          style={[
            flexbox.directionRow,
            flexbox.wrap,
            flexbox.alignCenter,
            spacings.ptMd,
            { columnGap: 16, rowGap: 8 }
          ]}
        >
          <TouchableOpacity
            style={[flexbox.directionRow, flexbox.alignCenter]}
            onPress={handleViewTransaction}
          >
            <Text
              testID="view-transaction-link"
              fontSize={textSize}
              appearance="secondaryText"
              weight="medium"
              style={spacings.mrMi}
              underline
            >
              {t('View transaction')}
            </Text>
            <LinkIcon width={iconSizeSm} height={iconSizeSm} />
          </TouchableOpacity>
          <TouchableOpacity
            style={[flexbox.directionRow, flexbox.alignCenter]}
            onPress={handleCopyTxHash}
          >
            <Text
              testID="copy-transaction-hash-link"
              fontSize={textSize}
              appearance="secondaryText"
              weight="medium"
              style={spacings.mrMi}
              underline
            >
              {t('Copy Transaction hash')}
            </Text>
            <CopyIcon width={iconSizeSm} height={iconSizeSm} />
          </TouchableOpacity>
          {isBlocked && onUnshieldToOrigin ? (
            <TouchableOpacity
              style={[flexbox.directionRow, flexbox.alignCenter]}
              onPress={onUnshieldToOrigin}
            >
              <Text
                fontSize={textSize}
                weight="semiBold"
                style={[spacings.mrMi, { color: theme.warningText as string }]}
                underline
              >
                {t('Unshield to origin')}
              </Text>
            </TouchableOpacity>
          ) : null}
          {isBlocked && ppoiUrl ? (
            <TouchableOpacity
              style={[flexbox.directionRow, flexbox.alignCenter]}
              onPress={() => createTab(ppoiUrl)}
            >
              <Text
                fontSize={textSize}
                appearance="secondaryText"
                weight="medium"
                style={spacings.mrMi}
                underline
              >
                {t('View on PPOI.info')}
              </Text>
              <LinkIcon width={iconSizeSm} height={iconSizeSm} />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    </View>
  )
}

export default Footer
